import { isUUID } from '@/types/Id'
import type { MemoryExcerpt, MemoryTarget } from '@/types/Memory'

type MemoryTargetFields = Pick<
  MemoryExcerpt,
  'organization_id' | 'pursuit_id' | 'file_id' | 'scope_kind'
>

interface MemoryTargetRouteValues {
  organizationId: MemoryTarget['organization_id']
  scopeKind?: string
  pursuitId?: string
  fileId?: string
}

export const getMemoryTargetFromExcerpt = (
  memory: MemoryTargetFields,
): MemoryTarget => {
  switch (memory.scope_kind) {
    case 'organization':
      return {
        scope_kind: 'organization',
        organization_id: memory.organization_id,
      }
    case 'pursuit':
      return {
        scope_kind: 'pursuit',
        organization_id: memory.organization_id,
        pursuit_id: memory.pursuit_id ?? '',
      }
    case 'file':
      return {
        scope_kind: 'file',
        organization_id: memory.organization_id,
        pursuit_id: memory.pursuit_id,
        file_id: memory.file_id ?? '',
      }
  }
}

export const getMemoryTargetFromRoute = ({
  organizationId,
  scopeKind,
  pursuitId,
  fileId,
}: MemoryTargetRouteValues): MemoryTarget | undefined => {
  if (!organizationId) {
    return undefined
  }

  switch (scopeKind) {
    case 'organization':
      return {
        scope_kind: 'organization',
        organization_id: organizationId,
      }
    case 'pursuit':
      return isUUID(pursuitId)
        ? {
            scope_kind: 'pursuit',
            organization_id: organizationId,
            pursuit_id: pursuitId,
          }
        : undefined
    case 'file': {
      if (!isUUID(fileId) || (pursuitId && !isUUID(pursuitId))) {
        return undefined
      }
      return {
        scope_kind: 'file',
        organization_id: organizationId,
        pursuit_id: isUUID(pursuitId) ? pursuitId : null,
        file_id: fileId,
      }
    }
    default:
      return undefined
  }
}

export const getMemoryDetailHref = (
  basePath: string,
  memory: Pick<MemoryExcerpt, 'id'> & MemoryTargetFields,
): string => {
  const path = `${basePath}/${memory.id}`
  const target = getMemoryTargetFromExcerpt(memory)

  if (target.scope_kind === 'organization') {
    return path
  }

  const params = new URLSearchParams({ memory_scope: target.scope_kind })
  if (target.pursuit_id) {
    params.set('memory_pursuit_id', target.pursuit_id)
  }
  if (target.scope_kind === 'file') {
    params.set('memory_file_id', target.file_id)
  }
  return `${path}?${params.toString()}`
}
